import * as React from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Loader } from 'semantic-ui-react';

import analytics from '../../shared/services/analytics/analytics';
import { getPerformanceComparison } from '../../actions/AlphasActions';
import { getSimulationPerformanceComparison } from '../../actions/Simulation/DataAction';
import AlphasAccordion from '../../components/AlphasAccordion/AlphasAccordion';
import PerformanceComparisonMain, {
  PerformanceComparisonCompetition,
} from '../../components/PerformanceComparison/PerformanceComparisonMain';
import { StateType } from '../../reducers';
import { activeTabIndexSelector, getTabsSelector } from '../../reducers/EditorReducer/EditorSelector';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';
import { ImmutableInterface } from '../../types';

import Immutable from 'immutable';

interface PerformanceComparisonProps {
  path: string[];
  id: string;
  isSimulation: boolean;
  size?: number;
  shouldResize?: number;
  position?: string;
}

const PerformanceComparison = (props: PerformanceComparisonProps) => {
  const { path, id, isSimulation, size, shouldResize, position } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();

  // @ts-expect-error ImmutableJS getIn returns unknown
  const delay: number = useSelector((state: StateType) => state.getIn(path.concat(['alphaDetail', 'settings', 'delay'])));
  const editorTabs: any = useSelector(getTabsSelector);
  const currentTabActive: any = useSelector(activeTabIndexSelector);
  // @ts-expect-error ImmutableJS getIn returns unknown
  const alphaId: string = useSelector((state: StateType) => state.getIn(path.concat(['alphaDetail', 'id'])));
  // @ts-expect-error ImmutableJS getIn returns unknown
  const isFetching: boolean = useSelector((state: StateType) => state.getIn(path.concat(['alphaDetail', 'performanceAlphaFetching'])));
  const performanceAlphaSummary = useSelector(
    (state: StateType) => state.getIn(path.concat(['alphaDetail', 'performanceAlphaSummary'])) as Immutable.List<any>
  );
  const performanceAlphaDatafieldsScore = useSelector(
    (state: StateType) => state.getIn(path.concat(['alphaDetail', 'performanceAlphaDatafieldsScore'])) as Immutable.List<any>
  );
  const performanceAlphaWeeklyScore = useSelector(
    (state: StateType) => state.getIn(path.concat(['alphaDetail', 'performanceAlphaWeeklyScore'])) as Immutable.List<any>
  );
  const performanceAlphaRobustnessScore = useSelector(
    (state: StateType) => state.getIn(path.concat(['alphaDetail', 'performanceAlphaRobustnessScore'])) as Immutable.List<any>
  );
  // @ts-expect-error ImmutableJS getIn returns unknown
  const performanceAlphaLastDate: string = useSelector((state: StateType) =>
    state.getIn(path.concat(['alphaDetail', 'performanceAlphaLastDate']))
  );
  // @ts-expect-error ImmutableJS getIn returns unknown
  const alphaDetail: Immutable.Collection<string, any> = useSelector((state: StateType) => state.getIn(path.concat(['alphaDetail'])));
  const partition: any = useSelector((state: StateType) => state.getIn(path.concat(['alphaDetail', 'performancePartition'])));
  const competition = useSelector(
    (state: StateType) =>
      state.getIn(path.concat(['alphaDetail', 'performanceCompetition'])) as ImmutableInterface<PerformanceComparisonCompetition> | null
  );

  const dispatchGetComparisonData = useCallback(
    async (alphaId: string, path: string[]) => {
      dispatch(getPerformanceComparison(alphaId, path));
    },
    [dispatch]
  );

  const dispatchGetSimulationComparisonData = useCallback(
    async (alphaId: string, idParent: string, idChild: string | null) => {
      dispatch(getSimulationPerformanceComparison(alphaId, idParent, idChild));
    },
    [dispatch]
  );

  const handleRefreshData = () => {
    analytics.trackCustomEvent('sim_click_refresh_performance');
    if (isSimulation) {
      const indexTab = currentTabActive;
      const tabId = editorTabs.getIn([indexTab, 'id']);
      const childIndex = editorTabs.getIn([indexTab, 'activeChildTab']);
      const childId = childIndex >= 0 ? editorTabs.getIn([indexTab, 'childTabs', childIndex, 'id']) : null;
      dispatchGetSimulationComparisonData(alphaId, tabId, childId);
    } else {
      dispatchGetComparisonData(alphaId, path);
    }
  };

  return (
    <div id={id} className="alphas-details-content__box">
      <div className="performance__title">
        <FormattedMessageHelper id="alphasDetails.title.performance-comparison" />
      </div>
      <AlphasAccordion
        performanceAlphaSummaryTitle="alphasDetails.performance-comparison.score.title"
        performanceAlphaSummaryTitleValues={{ delay: delay }}
        performanceAlphaSummary={performanceAlphaSummary}
        performanceAlphaDatafieldsScoreTitle="alphasDetails.performance-comparison.datafieldsScore.title"
        performanceAlphaDatafieldsScore={performanceAlphaDatafieldsScore}
        performanceAlphaWeeklyScoreTitle="alphasDetails.performance-comparison.weeklyScore.title"
        performanceAlphaWeeklyScore={performanceAlphaWeeklyScore}
        performanceAlphaRobustnessScoreTitle="alphasDetails.performance-comparison.robustnessScore.title"
        performanceAlphaRobustnessScore={performanceAlphaRobustnessScore}
        competition={competition}
        canRefresh={true}
        date={performanceAlphaLastDate}
        fetchingData={isFetching}
        handleRefreshData={handleRefreshData}
      >
        {isFetching ? (
          <div className="loader">
            <Loader active={true} inline="centered" size="massive" />
          </div>
        ) : (
          <PerformanceComparisonMain
            delay={delay}
            path={path}
            hasData={Boolean(performanceAlphaLastDate)}
            size={size}
            shouldResize={shouldResize}
            position={position}
            alphaDetail={alphaDetail}
            partition={partition}
            intl={intl}
            competition={competition}
          />
        )}
      </AlphasAccordion>
    </div>
  );
};

export default PerformanceComparison;
